/**
 * App root and route definitions.
 * Contains top-level routes used by the site.
 */
import "@/App.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./hooks/use-auth";
import logger from "./lib/logger";
import { frontendVersionInfo } from "./lib/version";
import { prefetchCustomerPricing } from "./lib/bootstrap-data";
import AmbientBackground from "./components/AmbientBackground";
import RouteSeo from "./components/RouteSeo";
import ScrollToTop from "./components/ScrollToTop";
import RouteFallback from "./components/RouteFallback";

const Landing = lazy(() => import("./pages/Landing"));
const Booking = lazy(() => import("./pages/Booking"));
const PayBooking = lazy(() => import("./pages/PayBooking"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Auth = lazy(() => import("./pages/Auth"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const Profile = lazy(() => import("./pages/Profile"));
const MyBookings = lazy(() => import("./pages/MyBookings"));
const MyEnquiries = lazy(() => import("./pages/MyEnquiries"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const SessionRoom = lazy(() => import("./pages/SessionRoom"));
const JoinSession = lazy(() => import("./pages/JoinSession"));
const CareersPage = lazy(() => import("./pages/CareersPage"));
const ServicesPlanPage = lazy(() => import("./pages/ServicesPlanPage"));

function App() {
  useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      logger.info("App mounted", frontendVersionInfo());
    }
    prefetchCustomerPricing();
  }, []);

  useEffect(() => {
    const el = document.getElementById("seo-static-content");
    if (el) el.innerHTML = "";
  }, []);

  return (
    <AuthProvider>
      <div className="App">
        <AmbientBackground />
        <BrowserRouter>
          <ScrollToTop />
          <RouteSeo />
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/book" element={<Booking />} />
              <Route path="/pay/booking/:bookingId" element={<PayBooking />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/careers/:roleType" element={<CareersPage />} />
              <Route path="/services/:planType" element={<ServicesPlanPage />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/my-bookings" element={<MyBookings />} />
              <Route path="/my-enquiries" element={<MyEnquiries />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/session/:bookingId" element={<SessionRoom />} />
              <Route path="/join/:joinToken" element={<JoinSession />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
        <Toaster position="top-right" richColors closeButton theme="light" />
      </div>
    </AuthProvider>
  );
}

export default App;
